import { CookieBanner } from "@/components/CookieBanner";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

type Lang = "es" | "en";

const NUDGE: Record<Lang, string> = {
  es: "¿Listo? Un toque y sos miembro",
  en: "Ready? One tap to join",
};

const PROCESSING: Record<Lang, string> = {
  es: "Preparando pago…",
  en: "Preparing checkout…",
};

const NOT_AVAILABLE: Record<Lang, string> = {
  es: "No disponible",
  en: "Not available",
};

export type MembershipVideoCtaPanelProps = {
  language: Lang;
  /** overlay = gradiente sobre el video (mobile). stacked = columna desktop al lado. */
  layout?: "overlay" | "stacked";
  /** Tras engagement con el video — CTA VIP más grande y visible. */
  engaged?: boolean;
  vipLabel: string;
  vipSubtitle?: string;
  showVip: boolean;
  showFree: boolean;
  freeLabel: string;
  freeUrl?: string | null;
  freeDisabled?: boolean;
  vipDisabled?: boolean;
  isCheckingOut?: boolean;
  checkoutAvailable?: boolean | null;
  onVipClick: () => void;
  onFreeClick?: () => void;
  vipTestId?: string;
  freeTestId?: string;
  /** Overrides i18n (DualCTA usa localT multi-idioma). */
  engagedNudge?: string;
  processingLabel?: string;
  notAvailableLabel?: string;
  /** Slot opcional entre free y cookies (trust badge, legal). */
  footerSlot?: ReactNode;
  /** Trust chips / micro-copy encima del botón VIP (mobile overlay). */
  preCtaSlot?: ReactNode;
};

/**
 * Panel CTA mobile-first bajo el video: VIP dominante, thumb zone segura,
 * cookies al final para no competir con el botón de pago.
 */
export function MembershipVideoCtaPanel({
  language,
  layout = "overlay",
  engaged = false,
  vipLabel,
  vipSubtitle,
  showVip,
  showFree,
  freeLabel,
  freeUrl,
  freeDisabled = false,
  vipDisabled = false,
  isCheckingOut = false,
  checkoutAvailable = true,
  onVipClick,
  onFreeClick,
  vipTestId = "membership-vip-cta",
  freeTestId = "membership-free-cta",
  engagedNudge,
  processingLabel,
  notAvailableLabel,
  footerSlot,
  preCtaSlot,
}: MembershipVideoCtaPanelProps) {
  const lang = language === "es" ? "es" : "en";
  const nudgeText = engagedNudge ?? NUDGE[lang];
  const processingText = processingLabel ?? PROCESSING[lang];
  const unavailableText = notAvailableLabel ?? NOT_AVAILABLE[lang];

  // checkoutAvailable=false pero no vipDisabled: estado de "no disponible temporalmente"
  // (MP down, Stripe config missing). Tratarlo visualmente igual que disabled.
  const vipUnavailable = checkoutAvailable === false && !vipDisabled;

  const isStacked = layout === "stacked";

  return (
    <div
      className={cn(
        "flex flex-col items-stretch",
        // Mobile overlay: gap más chico para que cookie + legal no empujen el VIP.
        isStacked ? "gap-3" : "gap-2",
        isStacked
          ? "relative z-[1] w-full max-w-sm"
          : cn(
              // z-[130] > Mux overlays (z-40) y loading dual (z-105) para que el
              // VIP membership no quede tapado en mobile/iOS/IG WebView.
              "absolute inset-x-0 bottom-0 z-[130] px-4 pointer-events-auto",
              "transition-[padding-top] duration-200 ease-out",
              engaged ? "pt-14" : "pt-10",
            ),
      )}
      style={
        isStacked
          ? undefined
          : {
              // Extra bottom padding en IG/iOS WebView: chrome nativo + home indicator.
              // + toolbar admin geo-sim (~bottom-right) no debe empujar el VIP fuera.
              paddingBottom:
                "max(1.75rem, calc(env(safe-area-inset-bottom, 0px) + 1.75rem))",
              background: engaged
                ? "linear-gradient(to top, rgba(0,0,0,0.96) 0%, rgba(0,0,0,0.75) 48%, rgba(0,0,0,0.25) 78%, rgba(0,0,0,0) 100%)"
                : "linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.62) 50%, rgba(0,0,0,0.18) 78%, rgba(0,0,0,0) 100%)",
            }
      }
      data-testid={isStacked ? "membership-cta-panel-stacked" : "membership-cta-panel-overlay"}
    >
      {preCtaSlot}
      {engaged && showVip && !vipDisabled && (
        <p
          className="text-center text-[13px] font-semibold tracking-tight text-amber-200/95 mb-1.5 animate-in fade-in duration-300"
          style={{ textShadow: "0 1px 4px rgba(0,0,0,0.85)" }}
        >
          {nudgeText}
        </p>
      )}

      {vipSubtitle && (
        <p
          className="text-[12px] text-white/65 font-medium tracking-wide text-center mb-0.5"
          style={{ textShadow: "0 1px 3px rgba(0,0,0,0.75)" }}
        >
          {vipSubtitle}
        </p>
      )}

      {showVip && (
        <button
          type="button"
          onClick={onVipClick}
          disabled={isCheckingOut || vipUnavailable || vipDisabled}
          aria-disabled={vipDisabled || vipUnavailable || undefined}
          className={cn(
            "w-full rounded-full",
            // Estado muted para vipDisabled y checkoutAvailable=false
            vipDisabled || vipUnavailable
              ? "bg-zinc-700/60 text-zinc-400 cursor-not-allowed"
              : cn(
                  "bg-gradient-to-r from-amber-400 to-yellow-300",
                  // hover solo desktop: en mobile sticky-hover pinta mal tras el tap
                  "md:hover:from-amber-300 md:hover:to-yellow-200 active:from-amber-500",
                  "text-amber-950 font-bold tracking-tight",
                  "shadow-[0_4px_20px_rgba(251,191,36,0.40)]",
                  "md:hover:shadow-[0_6px_24px_rgba(251,191,36,0.55)]",
                  engaged && "ring-2 ring-amber-300/55 shadow-[0_0_0_4px_rgba(251,191,36,0.12)]",
                ),
            // Mobile-first: VIP grande y tappable desde el primer paint (no
            // depender de engaged/video play para “aparecer” como CTA usable).
            isStacked
              ? "text-[17px] px-8 py-4"
              : "text-[16px] px-6 py-4",
            "transition-[transform,box-shadow,background-color,opacity] duration-150 active:scale-[0.98]",
            "disabled:opacity-60 disabled:cursor-wait disabled:animate-none",
          )}
          style={{
            touchAction: "manipulation",
            WebkitTapHighlightColor: "transparent",
            minHeight: isStacked ? 58 : 56,
          }}
          data-testid={vipTestId}
        >
          {isCheckingOut ? (
            <span className="flex items-center justify-center gap-2">
              <span className="h-4 w-4 rounded-full border-2 border-amber-900/30 border-t-amber-900 animate-spin" />
              {processingText}
            </span>
          ) : vipUnavailable || vipDisabled ? (
            unavailableText
          ) : (
            vipLabel
          )}
        </button>
      )}

      {showFree && (freeUrl || onFreeClick) && (
        freeUrl ? (
          <a
            href={freeDisabled ? undefined : freeUrl}
            target={freeDisabled ? undefined : "_blank"}
            rel={freeDisabled ? undefined : "noopener noreferrer"}
            aria-disabled={freeDisabled || undefined}
            onClick={(e) => {
              if (freeDisabled) {
                e.preventDefault();
                return;
              }
              onFreeClick?.();
            }}
            className={cn(
              "flex items-center justify-center w-full rounded-full no-underline",
              freeDisabled
                ? "bg-zinc-800/40 text-zinc-500 ring-1 ring-zinc-700 cursor-not-allowed select-none"
                : cn(
                    "bg-white/[0.12] md:hover:bg-white/[0.18] active:bg-white/[0.22]",
                    "ring-1 ring-white/20 backdrop-blur-md text-white",
                    engaged ? "opacity-85" : "",
                  ),
              engaged ? "text-[13px] font-medium py-2.5" : "text-[14px] font-semibold py-3",
              "px-6 transition-[transform,background-color,opacity] duration-150 active:scale-[0.98]",
            )}
            style={{
              touchAction: "manipulation",
              WebkitTapHighlightColor: "transparent",
              minHeight: 46,
            }}
            data-testid={freeTestId}
          >
            {freeLabel}
          </a>
        ) : (
          // Button-mode: no URL, navigation handled fully by onFreeClick
          <button
            type="button"
            disabled={freeDisabled}
            onClick={() => {
              if (!freeDisabled) onFreeClick?.();
            }}
            className={cn(
              "flex items-center justify-center w-full rounded-full",
              freeDisabled
                ? "bg-zinc-800/40 text-zinc-500 ring-1 ring-zinc-700 cursor-not-allowed select-none"
                : cn(
                    "bg-white/[0.12] md:hover:bg-white/[0.18] active:bg-white/[0.22]",
                    "ring-1 ring-white/20 backdrop-blur-md text-white",
                    engaged ? "opacity-85" : "",
                  ),
              engaged ? "text-[13px] font-medium py-2.5" : "text-[14px] font-semibold py-3",
              "px-6 transition-[transform,background-color,opacity] duration-150 active:scale-[0.98]",
            )}
            style={{
              touchAction: "manipulation",
              WebkitTapHighlightColor: "transparent",
              minHeight: 46,
            }}
            data-testid={freeTestId}
          >
            {freeLabel}
          </button>
        )
      )}

      {footerSlot}

      {!isStacked ? (
        // Delay alto: VIP primero. En IG WebView el user suele mirar el video
        // ~5–10s antes de interactuar; no empujar cookies al primer paint.
        <div className="mt-0.5 w-full">
          <CookieBanner variant="embedded" showDelayMs={engaged ? 5000 : 11000} />
        </div>
      ) : null}
    </div>
  );
}
